//App.js
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OrderAlertProvider } from './contexts/OrderAlertContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import OrderAlertOverlay from './components/OrderAlertOverlay';
import SubscriptionReminderModal from './components/SubscriptionReminderModal';
import SubscriptionExpiredPage from './pages/SubscriptionExpired/SubscriptionExpiredPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import notificationService from './services/notificationService';

import AdminRoutes from './routes/AdminRoutes';
import RestaurantRoutes from './routes/RestaurantRoutes';
import BranchRoutes from './routes/BranchRoutes';
import KitchenRoutes from './routes/KitchenRoutes';
import CashierRoutes from './routes/CashierRoutes';
import CaptainRoutes from './routes/CaptainRoutes';
import LoginRoutes from './routes/LoginRoutes';

// Self-service subscription renewal — reachable by every authenticated role,
// including gated roles whose normal routes are locked by the expiry guard.
const RenewSubscription = lazy(() => import('./pages/modules/admin/subscription/RenewSubscription/RenewSubscription'));
const RenewFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
  </div>
);

// Roles that get locked out when the restaurant subscription has expired.
// supadmin stays out of this set so billing management keeps working.
const GATED_ROLES = new Set(['restaurant', 'branch', 'kitchen', 'cashier', 'captain']);

function RoleBasedRoutes() {
  const { user, authState } = useAuth();

  console.log('\n🎯 RoleBasedRoutes - Rendering');
  console.log('  - authState:', authState);
  console.log('  - user:', user);
  console.log('  - Current Path:', window.location.pathname);

  // Show loading spinner while checking auth status
  if (authState.loading) {
    console.log('⏳ Auth loading...');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        Loading...
      </div>
    );
  }

  // If not authenticated, show login routes (includes all customer pages)
  if (!authState.isAuthenticated) {
    console.log('🚪 Not authenticated - Showing LoginRoutes (Customer + Auth pages)');
    return (
      <Routes>
        <Route path="/*" element={<LoginRoutes />} />
      </Routes>
    );
  }

  // If authenticated but no role, show LoginRoutes instead of redirecting
  // This allows customer pages to work even without admin authentication
  if (!user?.role) {
    console.log('⚠️ Authenticated but no role - Showing LoginRoutes');
    return (
      <Routes>
        <Route path="/*" element={<LoginRoutes />} />
      </Routes>
    );
  }

  // Role-based routing for authenticated admin/panel users
  console.log('✅ Authenticated with role:', user.role);
  return <RoleRoutesWithSubscriptionGuard role={user.role} />;
}

// Wraps the role-specific routes behind a subscription check. When the
// restaurant's subscription is not active, gated roles (restaurant, branch,
// kitchen, cashier, captain) are redirected to a fullscreen expired page.
// supadmin/admin routes are never gated so billing can be managed.
function RoleRoutesWithSubscriptionGuard({ role }) {
  const { status, loading } = useSubscription();
  const isGated = GATED_ROLES.has(role);
  const blocked = isGated && status && !status.exempt && status.expired === true;

  if (isGated && loading && !status) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        Loading…
      </div>
    );
  }

  if (blocked) {
    return (
      <Suspense fallback={<RenewFallback />}>
        <Routes>
          <Route path="/renew-subscription" element={<RenewSubscription />} />
          <Route path="*" element={<SubscriptionExpiredPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<RenewFallback />}>
      <Routes>
        <Route path="/renew-subscription" element={<RenewSubscription />} />
        {(role === "supadmin" || role === "admin") && <Route path="/*" element={<AdminRoutes />} />}
        {role === "restaurant" && <Route path="/*" element={<RestaurantRoutes />} />}
        {role === "branch" && <Route path="/*" element={<BranchRoutes />} />}
        {role === "kitchen" && <Route path="/*" element={<KitchenRoutes />} />}
        {role === "cashier" && <Route path="/*" element={<CashierRoutes />} />}
        {role === "captain" && <Route path="/*" element={<CaptainRoutes />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function MobileBrandingBadge() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHidden(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <a
      href="https://webdekho.in"
      target="_blank"
      rel="noopener noreferrer"
      className={`managed-by-badge d-lg-none ${hidden ? 'managed-by-badge-hidden' : ''}`}
    >
      <span style={{ fontSize: '0.6rem', opacity: 0.8 }}>Managed by</span>
      <img
        src="https://webdekho.in/images/wpl_rms.png"
        alt="Webdekho"
        className="managed-by-logo"
      />
    </a>
  );
}

function App() {
  // Initialize push notifications on app startup
  useEffect(() => {
    const initNotifications = async () => {
      const result = await notificationService.initialize();
      console.log('Notification initialization:', result);

      // Listen for push token registration
      const unsubscribe = notificationService.addListener('registration', ({ token }) => {
        console.log('Push token received:', token);
        // You can send this token to your backend here
        // Example: api.post('/api/devices/register', { token });
      });

      // Listen for push notifications received
      notificationService.addListener('pushReceived', ({ notification }) => {
        console.log('Push notification received in app:', notification);
      });

      // Listen for notification taps
      notificationService.addListener('pushTapped', ({ action }) => {
        console.log('User tapped notification:', action);
        // Handle navigation based on notification data
        if (action.notification?.data?.route) {
          // Navigate to the route specified in the notification
          window.location.href = action.notification.data.route;
        }
      });

      return unsubscribe;
    };

    initNotifications();

    return () => {
      notificationService.removeAllListeners();
    };
  }, []);

  return (
    <div className="App">
      <ToastContainer position="top-right" autoClose={3000} />
      <DarkModeProvider>
        <ThemeProvider>
          <Router>
            <AuthProvider>
              <SubscriptionProvider>
                <NotificationProvider>
                  <OrderAlertProvider>
                    <RoleBasedRoutes />
                    <OrderAlertOverlay />
                    <SubscriptionReminderModal />
                  </OrderAlertProvider>
                </NotificationProvider>
              </SubscriptionProvider>
            </AuthProvider>
          </Router>
        </ThemeProvider>
      </DarkModeProvider>
      <MobileBrandingBadge />
    </div>
  );
}

export default App;
